import type { ReactNode } from 'react';
import type { FileRoutesByPath } from '@tanstack/react-router';
import { createFileRoute } from '@tanstack/react-router';
import type {
  AppRoute,
  WidgetConfigEntry,
  WidgetPropsType,
} from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { EmptyWidget } from '~/components/widget/base/EmptyWidget';
import { useAppParams } from '~/helper/useAppParams';
import { useV3Redesign } from '~/helper/useV3Redesign';
import { V3EmptyState, V3ErrorState } from '~/components/v3/V3State';

export interface MakeQueryLoaderWidgetsRouteOptions {
  readonly isDemo?: boolean;
  readonly v3PageTitle?: string;
  readonly renderV3?: (widgetProps: WidgetPropsType) => ReactNode;
}

export function makeQueryLoaderWidgetsRoute(
  route: AppRoute,
  dynamicConfigWidgets: (widgetProps: WidgetPropsType) => WidgetConfigEntry[],
  // ponytail: boolean kept so existing demo routes stay untouched; migrate them to the object form when convenient.
  options: boolean | MakeQueryLoaderWidgetsRouteOptions = false,
  legacyRenderV3?: (widgetProps: WidgetPropsType) => ReactNode,
) {
  const {
    isDemo = false,
    v3PageTitle,
    renderV3 = legacyRenderV3,
  } = typeof options === 'boolean'
    ? { isDemo: options, v3PageTitle: undefined, renderV3: legacyRenderV3 }
    : options;
  const Route = createFileRoute(route as keyof FileRoutesByPath)({
    component: function Body() {
      const params = useAppParams(Route);
      const v3 = useV3Redesign() && v3PageTitle !== undefined;

      const query = useLoaderQuery(route, {
        isDemo,
        params: params,
      });

      if (query.isLoading) {
        return <LoadingWidget />;
      }

      if (query.isError) {
        if (v3) {
          return (
            <V3ErrorState
              title={v3PageTitle}
              onRetry={() => query.refetch()}
            />
          );
        }
        return (
          <ErrorWidget
            error={query.error}
            onRetry={() => query.refetch()}
            isRetrying={query.isFetching && !query.isLoading}
          />
        );
      }

      if (!query.data) {
        return v3 ? <V3EmptyState title={v3PageTitle} /> : <EmptyWidget />;
      }

      const data = query.data as {
        readonly widgetProps?: WidgetPropsType;
        readonly v3Redesign?: boolean;
      };
      const widgetProps = data.widgetProps;

      if (
        !widgetProps ||
        typeof widgetProps !== 'object' ||
        Object.keys(widgetProps).length === 0
      ) {
        return v3 ? <V3EmptyState title={v3PageTitle} /> : <EmptyWidget />;
      }

      if (renderV3 && (data.v3Redesign || useV3Redesign())) {
        return <>{renderV3(widgetProps)}</>;
      }

      return <RenderWidgets widgets={dynamicConfigWidgets(widgetProps)} />;
    },
  });

  return Route;
}
